import axios from 'axios';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api'
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export const authAPI = {
  signup: (data) => API.post('/auth/signup', data),
  login: (data) => API.post('/auth/login', data),
  changePassword: (data) => API.post('/auth/change-password', data),
  forgotPassword: (data) => API.post('/auth/forgot-password', data),
  resetPassword: (token, data) => API.put(`/auth/reset-password/${token}`, data)
};

export const projectAPI = {
  create: (data) => API.post('/projects', data),
  getAll: (params) => API.get('/projects', { params }),
  getById: (id) => API.get(`/projects/${id}`),
  update: (id, data) => API.put(`/projects/${id}`, data),
  delete: (id) => API.delete(`/projects/${id}`),
  getStats: () => API.get('/projects/stats'),
  addMilestone: (id, data) => API.post(`/projects/${id}/milestones`, data),
  updateMilestone: (id, milestoneId, data) => API.put(`/projects/${id}/milestones/${milestoneId}`, data),
  getGanttData: (params) => API.get('/projects/gantt', { params })
};

export const clientAPI = {
  create: (data) => API.post('/clients', data),
  getAll: (params) => API.get('/clients', { params }),
  getById: (id) => API.get(`/clients/${id}`),
  update: (id, data) => API.put(`/clients/${id}`, data),
  delete: (id) => API.delete(`/clients/${id}`)
};

export const taskAPI = {
  create: (data) => API.post('/tasks', data),
  getAll: (params) => API.get('/tasks', { params }),
  getById: (id) => API.get(`/tasks/${id}`),
  update: (id, data) => API.put(`/tasks/${id}`, data),
  delete: (id) => API.delete(`/tasks/${id}`),
  addComment: (id, data) => API.post(`/tasks/${id}/comments`, data),
  updateSubtask: (id, data) => API.put(`/tasks/${id}/subtasks`, data),
  bulkUpdate: (data) => API.post('/tasks/bulk-update', data)
};

export const userAPI = {
  create: (data) => API.post('/users', data),
  getAll: (params) => API.get('/users', { params }),
  getById: (id) => API.get(`/users/${id}`),
  update: (id, data) => API.put(`/users/${id}`, data),
  delete: (id) => API.delete(`/users/${id}`),
  deleteUser: (id) => API.delete(`/users/${id}`), // Alias for Team.jsx
  permanentlyDelete: (id) => API.delete(`/users/${id}/permanent`),
  resetPassword: (id, data) => API.put(`/users/${id}`, data), // resetPassword uses update endpoint
  downloadTemplate: () => API.get('/users/template', { responseType: 'blob' }),
  bulkImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return API.post('/users/bulk-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  uploadProfileImage: (file) => {
    const formData = new FormData();
    formData.append('profileImage', file);
    return API.post('/users/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  }
};

export const timeAPI = {
  punchIn: () => API.post('/time/punch-in'),
  punchOut: (data) => API.post('/time/punch-out', data),
  getStatus: () => API.get('/time/status'),
  getHistory: (params) => API.get('/time/history', { params }),
  getTeamAnalytics: (params) => API.get('/time/team-analytics', { params })
};

export const hrAPI = {
  getDashboardStats: () => API.get('/hr/dashboard/stats'),
  getRecentEmployees: () => API.get('/hr/dashboard/employees'),
  getAttendanceOverview: () => API.get('/hr/dashboard/attendance'),
  getEmployeesNearLimit: () => API.get('/hr/attendance/near-limit'),
  resetLateCounts: (employeeId) => API.post('/hr/attendance/reset-late-counts', { employeeId })
};

export const attendanceAPI = {
  getTodayAttendance: (params) => API.get('/attendance/today', { params }),
  getMonthlyAttendance: (params) => API.get('/attendance/monthly', { params }),
  getStats: (params) => API.get('/attendance/stats', { params }),
  markAttendance: (data) => API.post('/attendance/mark', data),
  bulkMarkAttendance: (data) => API.post('/attendance/bulk-mark', data),
  updateAttendance: (id, data) => API.put(`/attendance/${id}`, data),
  deleteAttendance: (id) => API.delete(`/attendance/${id}`),
  exportAttendance: (params) => API.get('/attendance/export', { params, responseType: 'blob' }),
  getEmployeeReport: (params) => API.get('/attendance/employee-report', { params })
};

export const settingsAPI = {
  getProfile: () => API.get('/settings/profile'),
  updateProfile: (data) => API.put('/settings/profile', data),
  changePassword: (data) => API.put('/settings/password', data),
  getOrganization: () => API.get('/settings/organization'),
  updateOrganization: (data) => API.put('/settings/organization', data),
  getNotifications: () => API.get('/settings/notifications'),
  updateNotifications: (data) => API.put('/settings/notifications', data),
  getAttendanceSettings: () => API.get('/hr/attendance/settings'),
  updateAttendanceSettings: (data) => API.put('/hr/attendance/settings', data)
};

export const dashboardAPI = {
  getDeveloperStats: () => API.get('/dashboard/developer/stats'),
  getDeveloperTasks: (params) => API.get('/dashboard/developer/tasks', { params }),
  getDeveloperActivity: (limit) => API.get('/dashboard/developer/activity', { params: { limit } }),
  getDeveloperProductivity: (days) => API.get('/dashboard/developer/productivity', { params: { days } }),
  getOperationsHeadStats: () => API.get('/dashboard/operations-head/stats'),
  getTeamPerformance: () => API.get('/dashboard/operations-head/team-performance'),
  getProjectOverview: () => API.get('/dashboard/operations-head/project-overview'),
  getRecentActivities: (limit) => API.get('/dashboard/operations-head/activities', { params: { limit } })
};

export const leaveAPI = {
  applyLeave: (data) => API.post('/leaves/apply', data),
  getMyLeaves: () => API.get('/leaves/my-leaves'),
  getAllLeaves: () => API.get('/leaves/all'),
  updateLeaveStatus: (id, data) => API.put(`/leaves/${id}/status`, data),
  getLeaveBalance: () => API.get('/leaves/balance')
};

export const daysheetAPI = {
  submit: (data) => API.post('/daysheets/submit', data),
  checkStatus: () => API.get('/daysheets/check-status'),
  getMyDaysheets: (params) => API.get('/daysheets/my-daysheets', { params }),
  getAll: (params) => API.get('/daysheets/all', { params }),
  getTeam: (params) => API.get('/daysheets/team', { params }),
  getById: (id) => API.get(`/daysheets/${id}`),
  update: (id, data) => API.put(`/daysheets/${id}`, data),
  getReport: (params) => API.get('/daysheets/report', { params }),
  export: (params) => API.get('/daysheets/export', { params, responseType: 'blob' })
};

export const teamAPI = {
  create: (data) => API.post('/teams', data),
  getAll: (params) => API.get('/teams', { params }),
  getById: (id) => API.get(`/teams/${id}`),
  update: (id, data) => API.put(`/teams/${id}`, data),
  delete: (id) => API.delete(`/teams/${id}`),
  addMember: (id, data) => API.post(`/teams/${id}/members`, data),
  removeMember: (id, memberId) => API.delete(`/teams/${id}/members/${memberId}`),
  getAvailableUsers: (id) => API.get(`/teams/${id}/available-users`)
};

export const taskTimeAPI = {
  startTimer: (data) => API.post('/task-time/start', data),
  stopTimer: (data) => API.post('/task-time/stop', data),
  getActiveTask: () => API.get('/task-time/active'),
  getTaskStatus: (taskId) => API.get(`/task-time/status/${taskId}`),
  getMyLogs: () => API.get('/task-time/my-logs'),
  getTeamLogs: () => API.get('/task-time/team-logs'),
  getTeamActiveTasks: () => API.get('/task-time/team-active'),
  getOperationalAnalytics: () => API.get('/task-time/operational-analytics')
};

export default API;
